/*
  Reel preview — riproduce un teaser video muto in loop (max 5-6s) ricavato da
  un video prodotto (es. reel Instagram/Facebook caricato come media Shopify).

  - context "card":    parte in hover (desktop) o quando la card è ben visibile
                       (touch/mobile), poster statico finché non parte.
  - context "product": parte in autoplay muto quando lo slide è in vista.

  Lazy: il file video viene scaricato solo al primo play (preload="none").
  Rispetta prefers-reduced-motion (in quel caso non parte nulla).
*/
if (!customElements.get('reel-preview')) {
  customElements.define(
    'reel-preview',
    class ReelPreview extends HTMLElement {
      constructor() {
        super();
        this.video = this.querySelector('video');
        this.context = this.dataset.context || 'card';
        this.maxSeconds = parseFloat(this.dataset.maxSeconds) || 6;
        this.loaded = false;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      connectedCallback() {
        if (!this.video || this.reduceMotion) return;

        // Loop "corto": riavvolge a 0 una volta raggiunto il limite di secondi.
        this.video.addEventListener('timeupdate', () => {
          if (this.video.currentTime >= this.maxSeconds) {
            this.video.currentTime = 0;
          }
        });

        if (this.context === 'card') {
          this.initCard();
        } else {
          this.initProduct();
        }
      }

      disconnectedCallback() {
        if (this.io) this.io.disconnect();
      }

      load() {
        if (this.loaded) return;
        this.loaded = true;
        const src = this.video.dataset.src;
        if (!src) return;
        const source = document.createElement('source');
        source.src = src;
        source.type = 'video/mp4';
        this.video.appendChild(source);
        this.video.load();
      }

      play() {
        this.load();
        const promise = this.video.play();
        if (promise && promise.then) {
          promise.then(() => this.classList.add('is-playing')).catch(() => {});
        } else {
          this.classList.add('is-playing');
        }
      }

      pause() {
        this.video.pause();
      }

      stop() {
        this.classList.remove('is-playing');
        this.video.pause();
        try {
          this.video.currentTime = 0;
        } catch (e) {
          /* alcuni browser non permettono il seek prima del caricamento */
        }
      }

      initCard() {
        const card = this.closest('.card-wrapper') || this.parentElement;
        if (!card) return;
        const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

        if (canHover) {
          card.addEventListener('mouseenter', () => this.play());
          card.addEventListener('mouseleave', () => this.stop());
          card.addEventListener('focusin', () => this.play());
          card.addEventListener('focusout', () => this.stop());
        } else {
          // Touch/mobile: parte quando la card è abbondantemente in vista.
          this.observe(0.6, false);
        }
      }

      initProduct() {
        // Autoplay muto quando lo slide del video entra in vista.
        this.observe(0.5, true);
      }

      observe(threshold, keepLoaded) {
        if (!('IntersectionObserver' in window)) {
          this.play();
          return;
        }
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
                this.play();
              } else if (keepLoaded) {
                this.pause();
              } else {
                this.stop();
              }
            });
          },
          { threshold: [0, threshold, 1] }
        );
        this.io.observe(this);
      }
    }
  );
}
